import React, { useEffect } from 'react';

import { Flex } from '@chakra-ui/react';
import { Avatar, AvatarImage, Button, ChevronDown } from '@mesh.ai/rabbit';
import MediaView from 'components/feed/MediaView';
import { PostText } from 'components/feed/PostFeedItem';
import { formatDistanceToNow } from 'date-fns';
import { useDispatch } from 'react-redux';

import NoFeedback from 'assets/assetsV1/illustrations/newIllustrations/noFeedback.svg';

import { FEEDBACK_FORM_VISIBILITY_RABBIT } from 'constants/ProjectConstants';

import { ChipSelect } from 'uiToolkitV1/ChipSelect';
import { Divider } from 'uiToolkitV1/Divider';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import {
  setFeedbackType,
  useAllPostsFetched,
  useCompetencyDrawerState,
  useOffset,
  usePosts,
} from '../competencyDrawerSlice';
import { useGetPostsMutation } from '../dataHooks';
import { CompetencyDrawerState } from '../types/slice';
import { FeedbackCTA } from './FeedbackCTA';

const FEEDBACK_TABS: Array<{ id: CompetencyDrawerState['feedbackType']; text: 'Praise' | 'Feedback' }> = [
  {
    id: 'praise',
    text: 'Praise',
  },
  {
    id: 'feedback',
    text: 'Feedback',
  },
];

const limit = 3;

export const Posts = () => {
  const dispatch = useDispatch();
  const posts = usePosts();
  const { feedbackType, isOpen } = useCompetencyDrawerState();
  const offset = useOffset();
  const isAllPostsFetched = useAllPostsFetched();

  const { isLoading, mutate: fetchPosts } = useGetPostsMutation();

  const fetchMorePosts = () => {
    fetchPosts({
      feedbackType: feedbackType === 'praise' ? 'KUDOS' : 'FEEDBACK',
      limit,
      offset,
    });
  };

  useEffect(() => {
    if (isOpen && posts.length === 0) {
      fetchPosts({
        feedbackType: feedbackType === 'praise' ? 'KUDOS' : 'FEEDBACK',
        limit,
        offset,
      });
    }
  }, [feedbackType, fetchPosts, isOpen, offset, posts.length]);

  return (
    <Flex flexDir="column" p={3} gap={5}>
      <Flex gap={1} alignItems="center">
        <TextLabel bold color="text.g3">
          Filter by:{' '}
        </TextLabel>
        <ChipSelect
          options={FEEDBACK_TABS}
          selectedIds={[feedbackType]}
          onChange={(e) => dispatch(setFeedbackType(e[0]))}
        />
      </Flex>
      {posts?.map((fb, i) => (
        <>
          <Flex key={fb.post.id} flexDir="column" gap={4}>
            <Flex gap={2} alignItems="center">
              <Avatar>
                <AvatarImage src={fb.post.from.profile.profilePhoto} />
              </Avatar>
              <Flex flexDir="column">
                <TextBodyMedium bold>{fb.post.from.metadata.displayName}</TextBodyMedium>
                <TextLabel>
                  {formatDistanceToNow(new Date(fb.post.createdTS), { addSuffix: true })} •{' '}
                  {FEEDBACK_FORM_VISIBILITY_RABBIT.find((t) => t.value === fb.post.feedbackVisibility)?.label ??
                    'Public'}
                </TextLabel>
              </Flex>
            </Flex>
            <PostText text={fb.post.text} macrosRes={fb.post.macros} className="" onPostClick={() => {}} />
            <MediaView media={fb.post.media} />
          </Flex>
          {i !== posts.length - 1 && <Divider borderColor="#EBEFF3" />}
        </>
      ))}
      {isLoading && <SkeletonLoader />}
      {!isAllPostsFetched && (
        <Button variant="text" iconRight={ChevronDown} onClick={fetchMorePosts}>
          See More
        </Button>
      )}
      {!posts?.length && (
        <EmptyScreen
          heading="No feedback to show. Request feedback"
          description="Nothing to see here yet. Request feedback for better performance enablement!"
          illustrationSrc={NoFeedback}
          CTAComp={FeedbackCTA}
        />
      )}
    </Flex>
  );
};
